import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { Session } from "../../../../../store/admin/sessionsStore";
import type { ThemeClasses } from "../../../shared";
import {
  formatLocation,
  getStatusInfo,
} from "../../../../../store/admin/sessionsStore";
import {
  formatDate,
  getDuration,
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../utils/getDate";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
interface SessionCardProps {
  session: Session;
  themeClasses: ThemeClasses;
  onView: () => void;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

const getPlatformInfo = (
  platform: string,
): { icon: string; color: string; bgColor: string; label: string } => {
  const platformLower = platform?.toLowerCase() || "";

  if (
    platformLower.includes("ios") ||
    platformLower.includes("iphone") ||
    platformLower.includes("ipad")
  ) {
    return {
      icon: "ri-apple-line",
      color: "text-[#F97316]",
      bgColor: "bg-[#F973161A]",
      label: "iOS App",
    };
  }

  if (platformLower.includes("android")) {
    return {
      icon: "ri-android-line",
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      label: "Android App",
    };
  }

  if (platformLower.includes("web")) {
    return {
      icon: "ri-computer-line",
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      label: "Web Dashboard",
    };
  }

  return {
    icon: "ri-device-line",
    color: "text-gray-500",
    bgColor: "bg-gray-100",
    label: platform || "Unknown",
  };
};

const getRoleInfo = (
  _role: string,
  platformColor: string,
): { bg: string; text: string } => {
  return {
    bg: platformColor
      .replace("text-", "bg-")
      .replace("500", "50")
      .replace("[#F97316]", "[#F973161A]"),
    text: platformColor,
  };
};

export default function SessionCard({
  session,
  themeClasses,
  onView,
  onLogout,
  isLoggingOut = false,
}: SessionCardProps) {
  const { t } = useTranslation("adminSections");
  const { t: tLite } = useTranslation("lite");
  const { isMonitor, isReadOnly } = useIsMonitor();

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite("time.noData"),
      rightNow: tLite("time.rightNow"),
      min: tLite("time.min"),
      hour: tLite("time.hour"),
      hours: tLite("time.hours"),
      day: tLite("time.day"),
      days: tLite("time.days"),
      month: tLite("time.month"),
      months: tLite("time.months"),
      and: tLite("time.and"),
    }),
    [tLite],
  );

  const platformInfo = getPlatformInfo(session.platform);
  const statusInfo = getStatusInfo(session.active, session.endedAt, t);
  const roleInfo = getRoleInfo(session.role, platformInfo.color);
  const location = formatLocation(session.location);
  const duration = getDuration(session.createdAt, session.endedAt);
  const lastActivity = getTimeAgo(session.updatedAt._seconds, timeTranslations);

  const roleUser = {
    admin: t("sessions.roles.admin"),
    driver: t("sessions.roles.driver"),
    monitor: t("sessions.roles.monitor"),
    "admin reseller": t("sessions.roles.adminReseller"),
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`w-11 h-11 rounded-lg flex items-center justify-center ${platformInfo.bgColor}`}
          >
            <i
              className={`${platformInfo.icon} text-xl ${platformInfo.color}`}
            ></i>
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold text-sm ${themeClasses.text} truncate max-w-[140px]`}
            >
              {session.userName || "Usuario"}
            </h3>
            <p
              className={`text-xs ${themeClasses.textSecondary} truncate max-w-[140px]`}
            >
              {session.userEmail}
            </p>
          </div>
        </div>

        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}
        >
          {statusInfo.label}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span
          className={`px-2.5 py-1 rounded-md text-xs font-medium ${roleInfo.bg} ${roleInfo.text}`}
        >
          {roleUser[session.role as keyof typeof roleUser] || "user"}
        </span>
        <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
          {platformInfo.label}
        </span>
      </div>

      <div className="space-y-2.5 mb-5">
        <div className="flex items-center justify-between">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("sessions.fields.device")}:
          </span>
          <span
            className={`text-xs ${themeClasses.text} font-medium text-right max-w-[140px] truncate`}
          >
            {session.device || "-"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("sessions.fields.location")}:
          </span>
          <span
            className={`text-xs ${themeClasses.text} font-medium text-right max-w-[140px] truncate`}
          >
            {location}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("sessions.fields.ip")}:
          </span>
          <span
            className={`text-xs ${themeClasses.text} font-medium font-mono`}
          >
            {session.ip || "-"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("sessions.fields.duration")}:
          </span>
          <span className={`text-xs ${themeClasses.text} font-medium`}>
            {duration}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("sessions.fields.startTime")}:
          </span>
          <span className={`text-xs ${themeClasses.text} font-medium`}>
            {session.createdAt ? formatDate(session.createdAt._seconds) : "-"}
          </span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          className={`flex-1 py-2.5 px-4 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} text-sm font-medium transition-colors ${themeClasses.buttonHover}`}
          onClick={onView}
        >
          {t("sessions.actions.details")}
        </button>
        {!isMonitor && !isReadOnly && session.active && (
          <button
            onClick={onLogout}
            disabled={isLoggingOut}
            className={`py-2.5 px-4 bg-red-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 min-w-[100px] ${
              isLoggingOut
                ? "opacity-70 cursor-not-allowed"
                : "hover:bg-red-600"
            }`}
          >
            {isLoggingOut ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{t("sessions.actions.closing")}</span>
              </>
            ) : (
              t("sessions.actions.logout")
            )}
          </button>
        )}
      </div>

      <p
        className={`text-xs ${themeClasses.textSecondary} mt-4 pt-3 border-t ${themeClasses.cardBorder}`}
      >
        {t("sessions.lastActivity")}: {lastActivity}
      </p>
    </div>
  );
}
